/**
 * Abschnitt "Notizen & Gedanken" an Bereichs-Board, Projekt- und
 * Ziel-Detailseite (F-17, Leitlinie 5): die zugeordneten Notizen und
 * Gedanken aus Resources - anklickbar, von hier aus zuzuordnen und zu loesen.
 */
import { useLiveQuery } from 'dexie-react-hooks';
import { Link } from 'react-router-dom';
import { Sparkles, StickyNote, X } from 'lucide-react';

import { AssignSelect } from '@/ui/composites/AssignSelect';
import { db } from '@/db/schema';
import { loesen, zuordnen } from '@/db/repositories/sammlung';
import { ausGedanke, ausNotiz, herkunftLabel, istZugeordnet, verknuepftMit } from '@/domain/resources/sammlung';
import type { ID } from '@/domain/types';

type Art = 'bereich' | 'projekt' | 'ziel';

export function NotizenAbschnitt({ art, id }: { art: Art; id: ID }) {
  const daten = useLiveQuery(async () => {
    const [resources, thoughts, projects, goals] = await Promise.all([
      db.resources.toArray(), db.thoughts.toArray(), db.projects.toArray(), db.goals.toArray(),
    ]);
    const p = new Map(projects.map((x) => [x.id, x]));
    const g = new Map(goals.map((x) => [x.id, x]));
    // Gedanken zaehlen, sobald sie gemerkt sind oder schon irgendwo haengen.
    const alle = [...resources.map(ausNotiz), ...thoughts.filter((t) => t.gemerkt === 1 || istZugeordnet(ausGedanke(t))).map(ausGedanke)];
    return {
      verknuepft: alle.filter((e) => verknuepftMit(e, { art, id }, p, g))
        .sort((a, b) => Number(b.angeheftet) - Number(a.angeheftet) || b.geaendert.localeCompare(a.geaendert)),
      frei: alle.filter((e) => !istZugeordnet(e) && !e.archiviert),
    };
  }, [art, id]);
  if (!daten) return null;

  return (
    <div className="flex flex-col gap-2">
      {daten.verknuepft.length === 0 && <p className="text-sm text-ink-faint">Noch keine Notizen oder Gedanken zugeordnet.</p>}
      <ul className="flex flex-col gap-1.5">
        {daten.verknuepft.map((e) => {
          const Icon = e.art === 'gedanke' ? Sparkles : StickyNote;
          return (
            <li key={`${e.art}:${e.id}`} className="group flex items-center gap-2 text-sm">
              <Icon className="size-3.5 shrink-0 text-ink-faint" aria-hidden="true" />
              <Link to={`/ressourcen?${e.art}=${encodeURIComponent(e.id)}`} className="min-w-0 flex-1 truncate hover:underline">
                {e.titel || 'Ohne Titel'}
              </Link>
              <span className="shrink-0 text-[11px] text-ink-faint">{e.art === 'gedanke' ? `Gedanke · ${herkunftLabel(e.herkunft)}` : herkunftLabel(e.herkunft)}</span>
              <button type="button" onClick={() => void loesen(e.art, e.id, art)} aria-label={`„${e.titel}“ lösen`}
                className="grid size-6 shrink-0 place-items-center rounded text-ink-faint opacity-0 hover:text-ink focus:opacity-100 group-hover:opacity-100">
                <X className="size-3.5" />
              </button>
            </li>
          );
        })}
      </ul>
      <AssignSelect
        label="Notiz oder Gedanken zuordnen"
        options={daten.frei.map((e) => ({ id: `${e.art}:${e.id}`, label: `${e.art === 'gedanke' ? 'Gedanke' : 'Notiz'}: ${e.titel}` }))}
        onAssign={(wert) => {
          const [was, eintrag] = wert.split(/:(.*)/) as ['notiz' | 'gedanke', ID];
          void zuordnen(was, eintrag, { art, id });
        }}
        className="self-start"
      />
    </div>
  );
}
